import React, { useEffect } from "react";
import { View, Image, Pressable, StyleSheet, ImageBackground } from "react-native";
import { Text } from "react-native";
import { colors, textStyle, useScale, getOrbSize } from "../styles";
import { useSocketStore } from "../store/socketStore";
import { useFaceStore } from "../store/faceStore";
import OfflineToast from "./OfflineToast";
import { useNetworkStore } from "../store/networkStore";
import { trackView, trackButton, View as MetricView, Button } from "../services/metrics";
import GalleryIcon from "./icons/GalleryIcon";
import VoiceOrb from "./voice/VoiceOrb";
import StorybuilderLogo from "../assets/icons/storybuilder-logo.svg";

import storyBg from "../assets/story-bg.png";
import btnCreate from "../assets/icons/sparkle.png";
import btnEdit from "../assets/icons/ic-edit.png";

interface Props {
  onCreateNew: () => void;
  onResume: () => void;
  onGallery: () => void;
}

export default function LandingPage({ onCreateNew, onResume, onGallery }: Props) {
  const { s, scale } = useScale();
  const hasExistingStory = useSocketStore((st) => st.hasExistingStory);
  const sessionInfoReceived = useSocketStore((st) => st.sessionInfoReceived);
  const isConnected = useSocketStore((st) => st.isConnected);
  const isOffline = useNetworkStore((st) => st.isOffline);
  const accessBlocked = useSocketStore((st) => st.accessBlocked);
  const buttonsReady = isConnected && sessionInfoReceived && !accessBlocked;
  const editDisabled = !buttonsReady || !hasExistingStory;

  useEffect(() => { trackView(MetricView.Landing); }, []);
  useEffect(() => {
    if (buttonsReady && !isOffline) {
      useFaceStore.getState().setOrbMode("animated");
      useFaceStore.getState().setFTUEVariant("01");
    } else {
      useFaceStore.getState().setOrbMode("inactive");
      useFaceStore.getState().setFTUEVariant(null);
    }
    return () => {
      useFaceStore.getState().setOrbMode("inactive");
      useFaceStore.getState().setFTUEVariant(null);
    };
  }, [buttonsReady, isOffline]);

  const orbSize = getOrbSize(scale);
  const scaled = {
    galleryBtn: { height: s(80), borderRadius: s(32), paddingHorizontal: s(20), top: s(40), right: s(16), gap: s(8) },
    galleryFontSize: { fontSize: s(18) },
    logo: { width: s(300), height: s(76), marginBottom: s(24) },
    buttons: { gap: s(48), marginTop: s(24) },
    btn: { height: s(80), borderRadius: s(40), paddingHorizontal: s(20) },
    icon: { width: s(24), height: s(24) },
    galleryIcon: s(32),
    fontSize: { fontSize: s(24) },
  };

  return (
    <ImageBackground source={storyBg} style={styles.root} resizeMode="cover">
      <OfflineToast variant="light" />
      {buttonsReady && (
        <Pressable
          style={[styles.galleryBtn, scaled.galleryBtn]}
          onPress={() => { trackButton(MetricView.Landing, Button.Landing.Gallery); onGallery(); }}
          accessibilityLabel="My Creations"
          accessibilityRole="button"
        >
          <GalleryIcon size={scaled.galleryIcon} />
          <Text style={[styles.btnText, scaled.galleryFontSize]}>My Creations</Text>
        </Pressable>
      )}

      <StorybuilderLogo width={scaled.logo.width} height={scaled.logo.height} style={{ marginBottom: scaled.logo.marginBottom }} />

      <VoiceOrb size={orbSize} />

      {buttonsReady && (
        <View style={[styles.buttons, scaled.buttons, isOffline && { opacity: 0 }]} pointerEvents={isOffline ? "none" : "auto"} accessibilityElementsHidden={isOffline} importantForAccessibility={isOffline ? "no-hide-descendants" : "auto"}>
          <Pressable
            style={[styles.btn, scaled.btn]}
            onPress={() => { trackButton(MetricView.Landing, Button.Landing.CreateStory); onCreateNew(); }}
            accessibilityRole="button"
          >
            <Image source={btnCreate} style={scaled.icon} resizeMode="contain" />
            <Text style={[styles.btnText, scaled.fontSize]}>Create new story</Text>
          </Pressable>

          <Pressable
            style={[styles.btn, scaled.btn, { opacity: editDisabled ? 0.4 : 1 }]}
            onPress={() => { trackButton(MetricView.Landing, Button.Landing.ContinueStory); onResume(); }}
            disabled={editDisabled}
            accessibilityRole="button"
          >
            <Image source={btnEdit} style={scaled.icon} resizeMode="contain" />
            <Text style={[styles.btnText, scaled.fontSize]}>Continue last story</Text>
          </Pressable>
        </View>
      )}
    </ImageBackground>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.bg,
    alignItems: "center",
    justifyContent: "center",
  },
  galleryBtn: {
    position: "absolute",
    flexDirection: "row",
    backgroundColor: colors.primary,
    alignItems: "center",
    justifyContent: "center",
  },
  buttons: {
    flexDirection: "row",
    alignItems: "center",
  },
  btn: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: colors.primary,
    gap: 8,
  },
  btnText: {
    color: colors.white,
    ...textStyle.heading,
  },
});
